import { ColDef } from 'ag-grid-enterprise'
import { TFunction } from 'react-i18next'
import { MatchClassification, Transaction } from '../../../../pages/reporting/vouchers/types'
import { Dimension } from '../../../dimension/types/Dimension'
import { parseDate } from '../../../../config/dayjsConfig'
import { formatValueByType } from '../../../../utils/helpers'
import DateRangeColumnFilter from '../../../../shared/components/custom/Grid/Filter/DateRangeColumnFilter'
import { getClassificationColor } from '../../../icReconciliation/components/ClassificationTag'

const INVALID_ACCOUNT_CLASSIFICATIONS: MatchClassification[] = [
  MatchClassification.INVALID_INCOME_STATEMENT_ACCOUNT,
  MatchClassification.INVALID_ASSETS_ACCOUNT,
  MatchClassification.INVALID_LIABILITIES_ACCOUNT,
  MatchClassification.INVALID_ACCOUNT
]

const isAccountError = (classification?: MatchClassification) =>
  !!classification && INVALID_ACCOUNT_CLASSIFICATIONS.includes(classification)

export const getTransactionDimensionColumns = (
  transactions: Transaction[],
  withFilters = false
): ColDef<Transaction>[] => {
  const dimensionNames = [...new Set(transactions.flatMap(trans => trans.dimensions?.map(({ name }) => name) || []))]

  return dimensionNames.map(dimensionName => ({
    headerName: dimensionName,
    colId: `dimension_${dimensionName}`,
    field: 'dimensions',
    filter: withFilters ? 'agTextColumnFilter' : false,
    filterParams: withFilters ? { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['reset'] } : undefined,
    valueGetter: params => {
      const dimensions = params.data?.dimensions
      return dimensions
        ?.find(({ name }: Dimension) => dimensionName === name)
        ?.children?.map((d: Dimension) => d.name)
        .join(', ')
    }
  }))
}

export const getTransactionColumnDefs = (
  t: TFunction,
  transactions: Transaction[],
  withFilters = false
): ColDef<Transaction>[] => [
  {
    headerName: t('vouchersPage:transactionGuid'),
    field: 'guid',
    width: 120,
    filter: withFilters ? 'agTextColumnFilter' : false,
    filterParams: withFilters ? { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['reset'] } : undefined
  },
  {
    headerName: t('vouchersPage:accountCode'),
    field: 'accountCode',
    width: 80,
    filter: withFilters ? 'agTextColumnFilter' : false,
    filterParams: withFilters ? { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['reset'] } : undefined,
    cellStyle: params =>
      isAccountError(params.data?.classification)
        ? { backgroundColor: `${getClassificationColor(MatchClassification.INVALID_INCOME_STATEMENT_ACCOUNT)}1a` }
        : undefined
  },
  {
    headerName: t('vouchersPage:accountName'),
    field: 'accountName',
    width: 100,
    tooltipField: 'accountName',
    filter: withFilters ? 'agTextColumnFilter' : false,
    filterParams: withFilters ? { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['reset'] } : undefined
  },
  {
    headerName: t('vouchersPage:transactionDate'),
    field: 'date',
    width: 110,
    filter: withFilters ? DateRangeColumnFilter : false,
    filterParams: withFilters ? { buttons: ['reset'] } : undefined,
    valueFormatter: params => {
      const date = params.value
      return date ? parseDate(date)?.format('DD.MM.YYYY') || '' : ''
    }
  },
  {
    headerName: t('vouchersPage:debit'),
    field: 'debit',
    width: 90,
    type: 'numericColumn',
    sortable: false,
    filter: withFilters ? 'agTextColumnFilter' : false,
    filterParams: withFilters ? { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['reset'] } : undefined,
    filterValueGetter: params => (params.data?.debit != null ? String(params.data.debit) : ''),
    valueFormatter: params =>
      formatValueByType(params.value, undefined, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      })
  },
  {
    headerName: t('vouchersPage:credit'),
    field: 'credit',
    width: 90,
    type: 'numericColumn',
    sortable: false,
    filter: withFilters ? 'agTextColumnFilter' : false,
    filterParams: withFilters ? { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['reset'] } : undefined,
    filterValueGetter: params => (params.data?.credit != null ? String(params.data.credit) : ''),
    valueFormatter: params =>
      formatValueByType(params.value, undefined, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      })
  },
  {
    headerName: t('vouchersPage:transactionType'),
    field: 'type',
    width: 100,
    filter: withFilters ? 'agTextColumnFilter' : false,
    filterParams: withFilters ? { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['reset'] } : undefined
  },
  {
    headerName: t('vouchersPage:transactionDescription'),
    field: 'description',
    width: 200,
    tooltipField: 'description',
    filter: withFilters ? 'agTextColumnFilter' : false,
    filterParams: withFilters ? { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['reset'] } : undefined
  },
  ...getTransactionDimensionColumns(transactions, withFilters)
]
